import React, {
  memo, useCallback, useEffect, useMemo, useRef, useState,
} from 'react'

import { Button } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, object } from 'prop-types'

import FilterSearchInput from 'Pods/SearchPage/Components/Shared/FilterSearchInput/FilterSearchInput'
import useTranslate from 'Pods/SearchPage/Shared/useTranslate'
import { addStyle, clearStyles, removeStyle } from 'Pods/SearchPage/Store/actions/styles'
import { useSearchPageStore } from 'Pods/SearchPage/Store/index'

import { getStylesForAssetType } from './mock'

// 4 rows; must match the `style-filter__tags--collapsed` max-height in search_page.scss.
const COLLAPSED_MAX_HEIGHT = 108

function StyleContent({ searchInputRef = null, hideReset = false, collapsible = false }) {
  const t = useTranslate()

  const assetType = useSearchPageStore(state => state.assetType)
  const selectedStyles = useSearchPageStore(state => state.styles)
  const [searchTerm, setSearchTerm] = useState('')
  const [showAll, setShowAll] = useState(false)
  const [hasOverflow, setHasOverflow] = useState(false)
  const tagsRef = useRef(null)
  // Initialized with current assetType so the effect skips on mount; only fires on real changes.
  // Only the local search term resets here - the styles themselves are cleared by setAssetType
  // (user-initiated changes only), so popstate restores keep their hydrated styles.
  const previousAssetTypeRef = useRef(assetType)

  useEffect(() => {
    if (previousAssetTypeRef.current === assetType) return
    previousAssetTypeRef.current = assetType
    setSearchTerm('')
  }, [assetType])

  const availableStyles = useMemo(() => {
    const all = getStylesForAssetType(assetType)
    if (!searchTerm) return all
    const normalized = searchTerm.toLowerCase()
    return all.filter(style => style.toLowerCase().includes(normalized))
  }, [assetType, searchTerm])

  // Surface the toggle only when the tags exceed 4 rows; remeasure on width and list changes.
  useEffect(() => {
    if (!collapsible) return undefined
    const el = tagsRef.current
    // Filtering to no matches unmounts the list; drop the stale toggle instead of keeping it
    // under the "No matches" text.
    if (!el) {
      setHasOverflow(false)
      return undefined
    }
    const measure = () => setHasOverflow(el.scrollHeight > COLLAPSED_MAX_HEIGHT + 1)
    measure()
    if (typeof ResizeObserver === 'undefined') return undefined
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [collapsible, availableStyles])

  const selectedSet = useMemo(() => new Set(selectedStyles), [selectedStyles])
  const hasSelectedAnyFilters = selectedStyles.length > 0
  const isCollapsed = collapsible && !showAll

  const handleToggle = (style) => {
    if (selectedSet.has(style)) {
      removeStyle(style)
    } else {
      addStyle(style)
    }
  }

  const handleToggleShowAll = useCallback(() => setShowAll(value => !value), [])

  const tagsClassName = isCollapsed ? 'style-filter__tags style-filter__tags--collapsed' : 'style-filter__tags'

  return (
    <div className="style-filter">
      <FilterSearchInput
        id="style-filter-search"
        value={searchTerm}
        onChange={setSearchTerm}
        placeholder={t('search_page.filters.style.find_placeholder')}
        inputRef={searchInputRef}
      />
      {availableStyles.length > 0 ? (
        <div
          ref={tagsRef}
          className={tagsClassName}
          role="group"
          aria-label={t('search_page.filters.style.tags_group_label')}
        >
          {availableStyles.map(style => (
            <Button
              key={style}
              type="secondary"
              size="tiny"
              isActive={selectedSet.has(style)}
              aria-pressed={selectedSet.has(style)}
              onClick={() => handleToggle(style)}
              customClass="style-filter__tag"
            >
              {style}
            </Button>
          ))}
        </div>
      ) : (
        <span className="style-filter__empty cgt-body-base">
          {t('search_page.filters.style.no_matches')}
        </span>
      )}
      {collapsible && hasOverflow && (
        <Button
          type="ghost-primary"
          size="small"
          customClass="filter-wrapper__show-more cgt-link-primary"
          onClick={handleToggleShowAll}
        >
          {t(`search_page.filters.style.${showAll ? 'show_less' : 'show_more'}`)}
        </Button>
      )}
      {!hideReset && (
        <Button
          type="ghost-primary"
          size="small"
          customClass="filter-wrapper__reset-button filter-wrapper__reset-button--inline"
          onClick={clearStyles}
          disabled={!hasSelectedAnyFilters}
        >
          {t('search_page.filters.style.reset')}
        </Button>
      )}
    </div>
  )
}

StyleContent.propTypes = {
  searchInputRef: object,
  hideReset: bool,
  collapsible: bool,
}

export default memo(StyleContent)
